import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sword, Shield, Wand2, Heart, Sparkles, ChevronRight, Loader2, Users, Scroll } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { ShareResult } from '@/components/social/ShareButtons';

type GamePhase = 'landing' | 'setup' | 'intro' | 'playing' | 'choice' | 'end';

interface Character {
  name: string;
  class: 'guerrier' | 'mage' | 'voleur' | 'guérisseur';
  hp: number;
  maxHp: number;
}

interface GameState {
  characters: Character[];
  currentScene: string;
  choices: string[];
  storyHistory: string[];
  turnCount: number;
}

const CLASS_INFO = {
  guerrier: { icon: Sword, color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/40' },
  mage: { icon: Wand2, color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/40' },
  voleur: { icon: Shield, color: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/40' },
  guérisseur: { icon: Heart, color: 'text-pink-400', bg: 'bg-pink-500/20', border: 'border-pink-500/40' },
};

const RpgIA = () => {
  const [phase, setPhase] = useState<GamePhase>('landing');
  const [isGenerating, setIsGenerating] = useState(false);
  const [characters, setCharacters] = useState<Character[]>([{ name: '', class: 'guerrier', hp: 100, maxHp: 100 }]);
  const [gameState, setGameState] = useState<GameState>({
    characters: [],
    currentScene: '',
    choices: [],
    storyHistory: [],
    turnCount: 0,
  });

  const addClass = () => {
    if (characters.length < 4) {
      setCharacters([...characters, { name: '', class: 'guerrier', hp: 100, maxHp: 100 }]);
    }
  };

  const removeClass = (index: number) => {
    if (characters.length > 1) {
      setCharacters(characters.filter((_, i) => i !== index));
    }
  };

  const updateCharacter = (index: number, field: keyof Character, value: string) => {
    const updated = [...characters];
    if (field === 'class') {
      updated[index] = { ...updated[index], class: value as Character['class'] };
    } else if (field === 'name') {
      updated[index] = { ...updated[index], name: value };
    }
    setCharacters(updated);
  };

  const generateStory = async (context: string, chars: Character[], history: string[] = []) => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-rpg-story', {
        body: { context, characters: chars, history },
      });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error generating story:', err);
      toast({
        title: 'Erreur',
        description: "Impossible de générer l'histoire. Réessayez.",
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const startGame = async () => {
    const validChars = characters.filter(c => c.name.trim());
    if (validChars.length === 0) {
      toast({
        title: 'Nom requis',
        description: 'Entrez au moins un nom de personnage.',
        variant: 'destructive',
      });
      return;
    }

    setPhase('intro');
    const story = await generateStory('introduction', validChars);
    
    if (story) {
      setGameState({
        characters: validChars,
        currentScene: story.scene,
        choices: story.choices || [],
        storyHistory: [story.scene],
        turnCount: 1,
      });
      setPhase('playing');
    } else {
      setPhase('setup');
    }
  };

  const makeChoice = async (choice: string) => {
    const newHistory = [...gameState.storyHistory, `Choix: ${choice}`];
    
    const story = await generateStory(choice, gameState.characters, newHistory);
    
    if (story) {
      const newTurnCount = gameState.turnCount + 1;
      
      if (story.isEnding || newTurnCount >= 10) {
        setGameState(prev => ({
          ...prev,
          currentScene: story.scene,
          choices: [],
          storyHistory: [...newHistory, story.scene],
          turnCount: newTurnCount,
        }));
        setPhase('end');
      } else {
        setGameState(prev => ({
          ...prev,
          currentScene: story.scene,
          choices: story.choices || [],
          storyHistory: [...newHistory, story.scene],
          turnCount: newTurnCount,
        }));
      }
    }
  };

  const resetGame = () => {
    setPhase('landing');
    setCharacters([{ name: '', class: 'guerrier', hp: 100, maxHp: 100 }]);
    setGameState({
      characters: [],
      currentScene: '',
      choices: [],
      storyHistory: [],
      turnCount: 0,
    });
  };

  return (
    <>
      <Helmet>
        <title>Aventure RPG - SuperSociety.ai</title>
        <meta name="description" content="Vivez une aventure épique générée par IA. Créez vos personnages et laissez l'histoire se dérouler selon vos choix." />
      </Helmet>

      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] rounded-full opacity-20"
            style={{
              background: 'radial-gradient(circle, hsl(270 67% 47% / 0.3) 0%, hsl(290 84% 50% / 0.2) 30%, transparent 60%)',
            }}
          />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-8 max-w-4xl">
          {/* Header */}
          <header className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Link>
            <div className="text-4xl">🗡️</div>
          </header>

          {/* Landing */}
          {phase === 'landing' && (
            <div className="text-center space-y-8 slide-up">
              <div className="space-y-4">
                <h1 className="font-display text-4xl md:text-6xl text-foreground">
                  Aventure <span className="text-violet-400">RPG</span>
                </h1>
                <p className="text-lg text-muted-foreground max-w-md mx-auto">
                  Une épopée narrative générée par IA où vos choix façonnent l'histoire
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20">
                  <Sparkles className="w-4 h-4 text-violet-400" />
                  Histoire générée par IA
                </span>
                <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20">
                  <Users className="w-4 h-4 text-violet-400" />
                  1-4 joueurs
                </span>
                <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20">
                  <Scroll className="w-4 h-4 text-violet-400" />
                  Choix multiples
                </span>
              </div>

              <Button
                onClick={() => setPhase('setup')}
                className="bg-violet-500 hover:bg-violet-600 text-white px-8 py-6 text-lg"
              >
                Commencer l'aventure
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}

          {/* Setup */}
          {phase === 'setup' && (
            <div className="space-y-8 slide-up">
              <div className="text-center space-y-2">
                <h2 className="font-display text-3xl text-foreground">Créez vos héros</h2>
                <p className="text-muted-foreground">Jusqu'à 4 personnages pour votre aventure</p>
              </div>

              <div className="space-y-4">
                {characters.map((char, index) => (
                  <div 
                    key={index}
                    className="p-4 rounded-xl bg-card/50 border border-border/50 space-y-4"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Personnage {index + 1}</span>
                      {characters.length > 1 && (
                        <button
                          onClick={() => removeClass(index)}
                          className="text-sm text-destructive hover:underline"
                        >
                          Retirer
                        </button>
                      )}
                    </div>

                    <Input
                      placeholder="Nom du personnage"
                      value={char.name}
                      onChange={(e) => updateCharacter(index, 'name', e.target.value)}
                      className="bg-background/50"
                    />

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {(Object.keys(CLASS_INFO) as Array<keyof typeof CLASS_INFO>).map((cls) => {
                        const info = CLASS_INFO[cls];
                        const Icon = info.icon;
                        const isSelected = char.class === cls;

                        return (
                          <button
                            key={cls}
                            onClick={() => updateCharacter(index, 'class', cls)}
                            className={`
                              p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2
                              ${isSelected 
                                ? `${info.bg} ${info.border} ${info.color}` 
                                : 'bg-background/30 border-border/30 text-muted-foreground hover:border-border'}
                            `}
                          >
                            <Icon className="w-6 h-6" />
                            <span className="text-xs capitalize">{cls}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {characters.length < 4 && (
                  <button
                    onClick={addClass}
                    className="w-full p-4 rounded-xl border-2 border-dashed border-border/50 text-muted-foreground hover:border-violet-500/50 hover:text-violet-400 transition-colors"
                  >
                    + Ajouter un personnage
                  </button>
                )}
              </div>

              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={() => setPhase('landing')}>
                  Retour
                </Button>
                <Button
                  onClick={startGame}
                  disabled={isGenerating}
                  className="bg-violet-500 hover:bg-violet-600 text-white"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Génération...
                    </>
                  ) : (
                    <>
                      Lancer l'aventure
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Intro loading */}
          {phase === 'intro' && (
            <div className="text-center space-y-6 slide-up">
              <Loader2 className="w-12 h-12 text-violet-400 animate-spin mx-auto" />
              <p className="text-lg text-muted-foreground">L'IA prépare votre aventure...</p>
            </div>
          )}

          {/* Playing */}
          {phase === 'playing' && (
            <div className="space-y-8 slide-up">
              {/* Characters status */}
              <div className="flex flex-wrap justify-center gap-3">
                {gameState.characters.map((char, index) => {
                  const info = CLASS_INFO[char.class];
                  const Icon = info.icon;
                  return (
                    <div 
                      key={index}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg ${info.bg} ${info.border} border`}
                    >
                      <Icon className={`w-4 h-4 ${info.color}`} />
                      <span className="text-sm text-foreground">{char.name}</span>
                    </div>
                  );
                })}
              </div>

              {/* Turn counter */}
              <div className="text-center">
                <span className="text-sm text-muted-foreground">Tour {gameState.turnCount}/10</span>
              </div>

              {/* Scene */}
              <div className="p-6 rounded-2xl bg-card/50 border border-violet-500/20 backdrop-blur-sm">
                <p className="text-lg text-foreground leading-relaxed whitespace-pre-line">
                  {gameState.currentScene}
                </p>
              </div>

              {/* Choices */}
              {!isGenerating && gameState.choices.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-center text-muted-foreground">Que faites-vous ?</p>
                  {gameState.choices.map((choice, index) => (
                    <button
                      key={index}
                      onClick={() => makeChoice(choice)}
                      className="w-full p-4 rounded-xl bg-violet-500/10 border border-violet-500/30 text-foreground hover:bg-violet-500/20 hover:border-violet-500/50 transition-all text-left"
                    >
                      {choice}
                    </button>
                  ))}
                </div>
              )}

              {isGenerating && (
                <div className="flex items-center justify-center gap-3 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>L'histoire se construit...</span>
                </div>
              )}
            </div>
          )}

          {/* End */}
          {phase === 'end' && (
            <div className="space-y-8 text-center slide-up">
              <div className="text-6xl">🏆</div>
              <h2 className="font-display text-3xl text-foreground">Fin de l'aventure</h2>
              
              <div className="p-6 rounded-2xl bg-card/50 border border-violet-500/20 backdrop-blur-sm text-left">
                <p className="text-lg text-foreground leading-relaxed whitespace-pre-line">
                  {gameState.currentScene}
                </p>
              </div>

              <ShareResult 
                gameName="Aventure RPG"
                score={gameState.turnCount * 100}
              />

              <Button
                onClick={resetGame}
                className="bg-violet-500 hover:bg-violet-600 text-white"
              >
                Nouvelle aventure
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RpgIA;
